export enum BarcodeType {
  GTIN_8 = 'GTIN_8',
  GTIN_12_UPC = 'GTIN_12_UPC',
  GTIN_13_EAN = 'GTIN_13_EAN',
  GTIN_14 = 'GTIN_14',
  CODE_128 = 'CODE_128',
  CODE_39 = 'CODE_39',
  QR_CODE = 'QR_CODE',
  DATA_MATRIX = 'DATA_MATRIX',
  PDF417 = 'PDF417',
  UNKNOWN = 'UNKNOWN',
}

export interface BarcodeValidationResult {
  isValid: boolean;
  type: BarcodeType;
  normalized: string;
  checkDigit?: string;
  error?: string;
  metadata?: {
    originalValue: string;
    detectedPattern: string;
    confidence: number;
  };
}

/**
 * Validates and analyzes a barcode value
 * Supports GTIN (8/12/13/14) with check digit validation and other formats
 */
export function validateBarcode(value: string): BarcodeValidationResult {
  const originalValue = value;

  // Clean and normalize the input
  const cleaned = cleanBarcodeValue(value);

  if (!cleaned) {
    return {
      isValid: false,
      type: BarcodeType.UNKNOWN,
      normalized: '',
      error: 'Empty or invalid barcode value',
      metadata: { originalValue, detectedPattern: 'empty', confidence: 0 },
    };
  }

  // Detect the barcode type
  const detectedType = detectBarcodeType(cleaned);

  // Perform validation based on type
  let validationResult: BarcodeValidationResult;

  switch (detectedType) {
    case BarcodeType.GTIN_8:
    case BarcodeType.GTIN_12_UPC:
    case BarcodeType.GTIN_13_EAN:
    case BarcodeType.GTIN_14:
      validationResult = validateGTIN(cleaned, detectedType);
      break;
    case BarcodeType.CODE_128:
    case BarcodeType.CODE_39:
      validationResult = validateAlphanumericCode(cleaned, detectedType);
      break;
    case BarcodeType.QR_CODE:
    case BarcodeType.DATA_MATRIX:
    case BarcodeType.PDF417:
      validationResult = validateComplexCode(cleaned, detectedType);
      break;
    default:
      validationResult = validateUnknownCode(cleaned);
      break;
  }

  // Add metadata
  validationResult.metadata = {
    originalValue,
    detectedPattern: getPatternDescription(cleaned),
    confidence: calculateConfidence(validationResult, cleaned),
  };

  return validationResult;
}

/**
 * Detects the barcode type based on length and pattern analysis
 * Based on our data analysis findings
 */
export function detectBarcodeType(value: string): BarcodeType {
  const length = value.length;
  const isNumeric = /^\d+$/.test(value);

  // Handle known problematic patterns from our analysis
  if (value === '111' || value === '无' || /^\d+\.\d+E[+-]\d+$/.test(value)) {
    return BarcodeType.UNKNOWN;
  }

  // URL or QR code patterns
  if (value.includes('http') || value.includes('www') || value.includes('://')) {
    return BarcodeType.QR_CODE;
  }

  // GTIN validation based on length and numeric content
  if (isNumeric) {
    switch (length) {
      case 8:
        return BarcodeType.GTIN_8;
      case 12:
        // Based on analysis: 43.9% of our data, mostly starting with 1 or 8
        return BarcodeType.GTIN_12_UPC;
      case 13:
        // Based on analysis: 36.7% of our data, various starting digits
        return BarcodeType.GTIN_13_EAN;
      case 14:
        return BarcodeType.GTIN_14;
      case 10:
      case 11:
        // Likely ISBN or other book codes
        return BarcodeType.UNKNOWN;
      default:
        if (length < 6) {
          // From analysis: many short codes like 7-char starting with 1
          return BarcodeType.UNKNOWN;
        }
        if (length > 14) {
          return BarcodeType.CODE_128;
        }
        return BarcodeType.UNKNOWN;
    }
  }

  // Alphanumeric codes
  if (/^[A-Z0-9\-_]+$/i.test(value)) {
    if (length >= 8 && length <= 48) {
      return BarcodeType.CODE_128;
    }
    if (length >= 4 && length <= 43) {
      return BarcodeType.CODE_39;
    }
  }

  return BarcodeType.UNKNOWN;
}

/**
 * Validates GTIN barcodes (8, 12, 13, 14 digit) with check digit verification
 * Implements GS1 check digit algorithm
 */
export function validateGTIN(value: string, type: BarcodeType): BarcodeValidationResult {
  const expectedLength = getExpectedGTINLength(type);

  if (value.length !== expectedLength) {
    return {
      isValid: false,
      type,
      normalized: value,
      error: `Invalid length: expected ${expectedLength} digits, got ${value.length}`,
    };
  }

  if (!/^\d+$/.test(value)) {
    return {
      isValid: false,
      type,
      normalized: value,
      error: 'GTIN must contain only digits',
    };
  }

  // Calculate and verify check digit
  const checkDigitResult = calculateGTINCheckDigit(value.slice(0, -1));
  const providedCheckDigit = value.slice(-1);

  if (checkDigitResult.checkDigit !== providedCheckDigit) {
    return {
      isValid: false,
      type,
      normalized: value,
      checkDigit: checkDigitResult.checkDigit,
      error: `Invalid check digit: expected ${checkDigitResult.checkDigit}, got ${providedCheckDigit}`,
    };
  }

  return {
    isValid: true,
    type,
    normalized: value,
    checkDigit: checkDigitResult.checkDigit,
  };
}

/**
 * Calculates GTIN check digit using GS1 algorithm
 */
export function calculateGTINCheckDigit(partialCode: string): {
  checkDigit: string;
  calculation: number[];
} {
  const digits = partialCode.split('').map(Number).reverse();
  const calculation: number[] = [];
  let sum = 0;

  for (let i = 0; i < digits.length; i++) {
    const multiplier = i % 2 === 0 ? 3 : 1;
    const product = (digits[i] ?? 0) * multiplier;
    calculation.push(product);
    sum += product;
  }

  const checkDigit = ((10 - (sum % 10)) % 10).toString();

  return {
    checkDigit,
    calculation: calculation.reverse(),
  };
}

/**
 * Validates alphanumeric codes (Code128, Code39)
 */
function validateAlphanumericCode(value: string, type: BarcodeType): BarcodeValidationResult {
  let isValid = true;
  let error: string | undefined;

  if (type === BarcodeType.CODE_39) {
    // Code39 supports: A-Z, 0-9, and special chars: - . $ / + % space
    if (!/^[A-Z0-9\-\.\$\/\+%\s]+$/.test(value.toUpperCase())) {
      isValid = false;
      error = 'Invalid characters for Code39';
    }
    if (value.length < 1 || value.length > 43) {
      isValid = false;
      error = 'Invalid length for Code39 (1-43 characters)';
    }
  } else if (type === BarcodeType.CODE_128) {
    // Code128 supports full ASCII set, length varies
    if (value.length < 1 || value.length > 80) {
      isValid = false;
      error = 'Invalid length for Code128 (1-80 characters)';
    }
  }

  return {
    isValid,
    type,
    normalized: value.toUpperCase(),
    error,
  };
}

/**
 * Validates complex 2D codes (QR, Data Matrix, PDF417)
 */
function validateComplexCode(value: string, type: BarcodeType): BarcodeValidationResult {
  // Basic validation for 2D codes
  let isValid = true;
  let error: string | undefined;

  if (value.length < 1) {
    isValid = false;
    error = 'Empty code value';
  } else if (value.length > 3000) {
    isValid = false;
    error = 'Code too long';
  }

  return {
    isValid,
    type,
    normalized: value,
    error,
  };
}

/**
 * Validates unknown or unrecognized barcodes
 */
function validateUnknownCode(value: string): BarcodeValidationResult {
  // Mark as valid but unknown type for manual review
  return {
    isValid: true,
    type: BarcodeType.UNKNOWN,
    normalized: value,
    error: 'Unrecognized barcode format - manual review recommended',
  };
}

/**
 * Cleans and normalizes barcode input
 */
function cleanBarcodeValue(value: string): string {
  if (!value) return '';

  return value
    .trim()
    .replace(/\s+/g, '') // Remove all whitespace
    .replace(/[^\w\-\.\$\/\+%:]/g, ''); // Keep only valid barcode characters
}

/**
 * Gets expected length for GTIN types
 */
function getExpectedGTINLength(type: BarcodeType): number {
  switch (type) {
    case BarcodeType.GTIN_8:
      return 8;
    case BarcodeType.GTIN_12_UPC:
      return 12;
    case BarcodeType.GTIN_13_EAN:
      return 13;
    case BarcodeType.GTIN_14:
      return 14;
    default:
      return 0;
  }
}

/**
 * Generates a pattern description for metadata
 */
function getPatternDescription(value: string): string {
  const length = value.length;
  const isNumeric = /^\d+$/.test(value);
  const firstChar = value.charAt(0);

  if (isNumeric) {
    return `${length} digits starting with '${firstChar}'`;
  }

  const hasLetters = /[a-zA-Z]/.test(value);
  const hasSpecial = /[^a-zA-Z0-9]/.test(value);

  let pattern = `${length} chars`;
  if (hasLetters && !hasSpecial) pattern += ' (alphanumeric)';
  else if (hasLetters && hasSpecial) pattern += ' (mixed with special chars)';
  else if (!hasLetters && hasSpecial) pattern += ' (numeric with special chars)';

  return pattern;
}

/**
 * Calculates confidence score for the validation result
 */
function calculateConfidence(result: BarcodeValidationResult, value: string): number {
  let confidence = 0.5; // Base confidence

  // Higher confidence for recognized patterns
  if (result.type !== BarcodeType.UNKNOWN) {
    confidence += 0.3;
  }

  // Higher confidence for valid codes
  if (result.isValid) {
    confidence += 0.2;
  }

  // Higher confidence for GTIN codes with valid check digits
  if (result.checkDigit && result.isValid) {
    confidence += 0.2;
  }

  // Lower confidence for very short or very long codes
  if (value.length < 6 || value.length > 20) {
    confidence -= 0.2;
  }

  // Lower confidence for known problematic patterns
  if (value === '111' || /^\d+\.\d+E[+-]\d+$/.test(value)) {
    confidence = 0.1;
  }

  return Math.max(0, Math.min(1, confidence));
}

/**
 * Convenience function to check if a barcode is valid
 */
export function isValidBarcode(value: string): boolean {
  return validateBarcode(value).isValid;
}

/**
 * Convenience function to get barcode type
 */
export function getBarcodeType(value: string): BarcodeType {
  return detectBarcodeType(cleanBarcodeValue(value));
}

/**
 * Convenience function to normalize barcode
 */
export function normalizeBarcode(value: string): string {
  return validateBarcode(value).normalized;
}
